import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconEdit, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadListCsv } from '../utils/csv';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';
import useCsvExport from '../hooks/useCsvExport';

// TS port of the source (Falah) app's pages/Assets.jsx. Route namespace is
// `/resources/assets/...` here (the source uses a flat `/assets`) —
// Resources, like Revenue and Procurement, gets its own namespace for
// consistency; Pass C wires the final nav so this is a naming choice this
// pass owns, not a verified Pass A precedent (unlike `/revenue/*` and
// `/procurement/*`, which UserDetail.tsx already assumed).
interface AssetRow {
  id: number;
  description: string;
  vendor: string | null;
  asset_tag: string | null;
  category: string | null;
  asset_type: 'fixed' | 'non_fixed';
  location: string | null;
  purchase_value: number | null;
  status: 'active' | 'pending_disposal' | 'disposed' | 'impaired';
}

const STATUS_LABEL: Record<AssetRow['status'], string> = {
  active: 'Active',
  pending_disposal: 'Pending Disposal',
  disposed: 'Disposed',
  impaired: 'Impaired',
};
const STATUS_BADGE: Record<AssetRow['status'], string> = {
  active: 'badge-active',
  pending_disposal: 'badge-scheduled',
  disposed: 'badge-urgent',
  impaired: 'badge-normal',
};

export function Assets() {
  const { lists, settings } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const [search, setSearch] = useState('');
  const [assetType, setAssetType] = useState('');
  const [category, setCategory] = useState('');

  const filters = useMemo(
    () => ({ search: search || undefined, asset_type: assetType || undefined, category: category || undefined }),
    [search, assetType, category]
  );
  const { data, loading, error, setPage, reload: load } = usePaginatedList<AssetRow>('/assets', filters);

  const remove = async (asset: AssetRow) => {
    if (!(await confirmDelete(`Delete "${asset.description}"?`))) return;
    try {
      await api.delete(`/assets/${asset.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const { exporting, exportCsv } = useCsvExport(() =>
    downloadListCsv<AssetRow>(
      api,
      '/assets',
      { search: search || undefined, asset_type: assetType || undefined, category: category || undefined },
      [
        { label: 'Description', value: (r) => r.description },
        { label: 'Vendor', value: (r) => r.vendor ?? '' },
        { label: 'Tag', value: (r) => r.asset_tag ?? '' },
        { label: 'Category', value: (r) => (r.category ? labelFor(lists, 'asset_category', r.category) : '') },
        { label: 'Type', value: (r) => (r.asset_type === 'fixed' ? 'Fixed' : 'Non-Fixed') },
        { label: 'Location', value: (r) => r.location ?? '' },
        { label: 'Purchase Value', value: (r) => r.purchase_value ?? '' },
      ],
      'assets.csv'
    )
  );

  return (
    <>
      <header className="page-header">
        <h1>Assets</h1>
        <Link className="btn btn-primary" to="/resources/assets/new">
          + Add asset
        </Link>
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search description, tag or serial…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={assetType} onChange={(e) => setAssetType(e.target.value)}>
          <option value="">All types</option>
          <option value="fixed">Fixed</option>
          <option value="non_fixed">Non-Fixed</option>
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {listOptions(lists, 'asset_category').map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <IconButton
          icon={IconDownload}
          label={exporting ? 'Exporting…' : 'Download CSV'}
          onClick={exportCsv}
          disabled={exporting}
        />
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th>Tag</th>
                <th>Category</th>
                <th>Type</th>
                <th>Status</th>
                <th>Location</th>
                <th className="right">Value</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <strong>{a.description}</strong>
                      {a.vendor && (
                        <div>
                          <small className="muted">{a.vendor}</small>
                        </div>
                      )}
                    </td>
                    <td className="muted">{a.asset_tag ?? '—'}</td>
                    <td>{a.category ? labelFor(lists, 'asset_category', a.category) : '—'}</td>
                    <td>
                      <span className={`badge badge-${a.asset_type === 'fixed' ? 'important' : 'normal'}`}>
                        {a.asset_type === 'fixed' ? 'Fixed' : 'Non-Fixed'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[a.status]}`}>{STATUS_LABEL[a.status]}</span>
                    </td>
                    <td>{a.location ?? '—'}</td>
                    <td className="right">{a.purchase_value ? fmtMoney(a.purchase_value) : '—'}</td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/resources/assets/${a.id}`} />
                      <IconButton icon={IconEdit} label="Edit" to={`/resources/assets/${a.id}/edit`} />
                      <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(a)} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="muted center">
                    No assets found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
